import { Link } from "react-router-dom";
import type { DocumentRef, PublicDocumentTag } from "@vedaunion/shared";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDocuments, useFeaturedLibrary } from "../../../lib/documents";
import { BhagavadGitaCompact } from "../../../components/landing/BhagavadGitaCompact";
import { LibraryCard } from "../../../components/document/LibraryCard";
import { useYoutubeLibrary } from "../../../lib/youtube";
import { GUIDES } from "../../../components/guide/guides";
import { GuideCard } from "./LibraryIntroduction";

/**
 * Library → All. The entry point, composed rather than filtered: admin-pinned
 * documents, the newest publications, a random scattering, per-tag spotlights,
 * a Bhagavad Gītā card, a strip of recent videos — and then EVERYTHING, in one
 * list that keeps going.
 *
 * That last part is the difference between a landing page and a library. A
 * curated sampler answers "what should I look at?", and then dead-ends: if what
 * you wanted was not in the sample you have to leave and go filter somewhere
 * else. So the page ends with the whole collection, sorted how you like it and
 * extended in place — the curation is the way IN, not a wall around the rest.
 */

export default function LibraryFeatured() {
  const featuredQ = useFeaturedLibrary();
  const f = featuredQ.data;
  const recentVideosQ = useYoutubeLibrary({ limit: 4, offset: 0, q: "", sort: "newest" });
  const recentVideos = recentVideosQ.data?.videos ?? [];

  if (featuredQ.isLoading) return <p className="text-[var(--color-ink-mute)]">Loading…</p>;

  const hasContent =
    !!f && (f.pinned.length > 0 || f.recent.length > 0 || f.randomDocuments.length > 0);

  return (
    <div className="flex flex-col gap-12">
      {/* Start here — the Introduction shelf's first guide: how every text
          below is marked and how to recite from it. */}
      {GUIDES[0] ? <GuideCard g={GUIDES[0]} /> : null}

      {/* Pinned — admin-curated. */}
      {f && f.pinned.length > 0 && (
        <FeaturedSection
          eyebrow="Pinned"
          title={
            <>
              On the table <span className="font-display-italic">today</span>.
            </>
          }
          hint="Documents the team wants you to find first — current classes, seasonal practices, fresh additions."
        >
          <ul className="featured-grid">
            {f.pinned.map((d) => (
              <li key={d.id}>
                <DocumentCard d={d} eyebrow="Pinned" />
              </li>
            ))}
          </ul>
        </FeaturedSection>
      )}

      {/* Recent + Bhagavad Gita */}
      <section>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8 items-start">
          <div>
            <header className="mb-5">
              <p className="page-eyebrow mb-1">Newly added</p>
              <h2 className="font-display text-[1.7rem] sm:text-[2rem] leading-[1.15] tracking-tight">
                Recently published
              </h2>
              <p className="font-grotesk text-[0.78rem] text-[var(--color-ink-soft)] mt-2 max-w-[60ch]">
                The latest documents to land in the Library — mantras,
                sūktas, sādhanas, and study guides.
              </p>
            </header>
            {f && f.recent.length > 0 ? (
              <ul className="featured-grid">
                {f.recent.slice(0, 4).map((d) => (
                  <li key={d.id}>
                    <DocumentCard d={d} eyebrow="New" />
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyHint message="No documents published yet — they'll appear here as they land." />
            )}
          </div>
          <BhagavadGitaCompact />
        </div>
      </section>

      {/* Tag spotlight — three groupings, each a row of 3-4 cards. */}
      {f && f.tagSpotlight.length > 0 && (
        <FeaturedSection
          eyebrow="By type"
          title={
            <>
              Browse by <span className="font-display-italic">form</span>.
            </>
          }
          hint="Different shapes of practice — pick the one that fits the morning you have."
        >
          <div className="flex flex-col gap-9">
            {f.tagSpotlight.map((s) => (
              <TagSpotlightRow key={s.tag.id} tag={s.tag} documents={s.documents} />
            ))}
          </div>
        </FeaturedSection>
      )}

      {/* Random — light surprise. */}
      {f && f.randomDocuments.length > 0 && (
        <FeaturedSection
          eyebrow="A small surprise"
          title="Wandering picks"
          hint="Things you might not search for, surfaced anyway."
        >
          <ul className="featured-grid">
            {f.randomDocuments.map((d) => (
              <li key={d.id}>
                <DocumentCard d={d} eyebrow="Pick" />
              </li>
            ))}
          </ul>
        </FeaturedSection>
      )}

      {/* Recent videos strip — link out to /dashboard/library/videos for more. */}
      {recentVideos.length > 0 && (
        <FeaturedSection
          eyebrow="On video"
          title="Latest from the channel"
          hint="The newest classes and gatherings on the Veda Union YouTube channel."
          actionTo="/dashboard/library/videos"
          actionLabel="All videos →"
        >
          <ul className="featured-grid">
            {recentVideos.map((v) => (
              <li key={v.id}>
                <a
                  href={`https://www.youtube.com/watch?v=${v.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="featured-card"
                >
                  {v.thumbnailUrl ? (
                    <img src={v.thumbnailUrl} alt="" className="featured-card-cover" loading="lazy" />
                  ) : (
                    <div className="featured-card-cover is-fallback">▶</div>
                  )}
                  <div className="featured-card-body">
                    <p className="featured-card-eyebrow">YouTube</p>
                    <p className="featured-card-title">{v.title}</p>
                  </div>
                </a>
              </li>
            ))}
          </ul>
        </FeaturedSection>
      )}

      {/* …and then all of it. */}
      <Everything />

      {!hasContent && recentVideos.length === 0 && (
        <div className="veda-empty">
          <p className="font-display text-[1.5rem] leading-[1.18] tracking-tight mb-2">
            The Library is just getting started.
          </p>
          <p className="text-[0.95rem] text-[var(--color-ink-soft)] max-w-[55ch] mx-auto leading-[1.55]">
            Documents, videos, and external study resources will appear here as
            they're added.
          </p>
        </div>
      )}
    </div>
  );
}

function FeaturedSection({
  eyebrow,
  title,
  hint,
  actionTo,
  actionLabel,
  children,
}: {
  eyebrow: string;
  title: React.ReactNode;
  hint?: string;
  actionTo?: string;
  actionLabel?: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <header className="mb-6 flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <p className="page-eyebrow mb-1">{eyebrow}</p>
          <h2 className="font-display text-[1.7rem] sm:text-[2rem] leading-[1.15] tracking-tight">
            {title}
          </h2>
          {hint && (
            <p className="font-grotesk text-[0.78rem] text-[var(--color-ink-soft)] mt-2 max-w-[60ch]">
              {hint}
            </p>
          )}
        </div>
        {actionTo && actionLabel && (
          <Link
            to={actionTo}
            className="font-grotesk text-[0.7rem] tracking-[0.16em] uppercase text-[var(--color-violet)] hover:text-[var(--color-ink)] transition-colors"
          >
            {actionLabel}
          </Link>
        )}
      </header>
      {children}
    </section>
  );
}

function TagSpotlightRow({
  tag,
  documents,
}: {
  tag: PublicDocumentTag;
  documents: DocumentRef[];
}) {
  return (
    <div>
      <header className="mb-3 flex items-baseline justify-between gap-3">
        <h3 className="font-display text-[1.3rem] leading-[1.2] tracking-tight">
          <span className="font-display-italic text-[var(--color-violet)]">{tag.label}</span>
          <span className="text-[var(--color-ink-mute)] mx-2">·</span>
          <span className="font-grotesk text-[0.7rem] tracking-[0.18em] uppercase text-[var(--color-ink-mute)]">
            {tag.namespace}
          </span>
        </h3>
        <Link
          to={`/library/documents?tag=${encodeURIComponent(`${tag.namespace}:${tag.slug}`)}`}
          className="font-grotesk text-[0.62rem] tracking-[0.16em] uppercase text-[var(--color-violet)] hover:text-[var(--color-ink)] transition-colors"
        >
          See all →
        </Link>
      </header>
      <ul className="featured-grid">
        {documents.map((d) => (
          <li key={d.id}>
            <DocumentCard d={d} eyebrow={tag.label} />
          </li>
        ))}
      </ul>
    </div>
  );
}

// Sorting is limited to what the LIST endpoint actually returns. Tags are
// filtered server-side and are not on a list item, so "by deity" would mean
// fetching every document to sort a page — the Documents tab's tag filter is
// the right tool for that, and it is one tab away.
type Sort = "newest" | "oldest" | "az";

const SORTS: ReadonlyArray<{ id: Sort; label: string }> = [
  { id: "newest", label: "Newest" },
  { id: "oldest", label: "Oldest" },
  { id: "az", label: "A–Z" },
];

const PAGE = 12;

/** The whole collection, sorted and extended in place. Sorting happens on the
 *  client because the list is small enough to hold and the alternative is a new
 *  server round trip for every change of mind, which is the opposite of what a
 *  browsing surface should feel like. */
function Everything() {
  const [sort, setSort] = useState<Sort>("newest");
  const [shown, setShown] = useState(PAGE);
  const q = useDocuments({ limit: 300 });
  const all = q.data?.documents ?? [];

  const sorted = useMemo(() => {
    const list = [...all];
    if (sort === "newest") return list;   // the server already returns newest-first
    if (sort === "oldest") return list.reverse();
    return list.sort((a, b) => a.title.localeCompare(b.title));
  }, [all, sort]);

  const sentinel = useRef<HTMLDivElement | null>(null);
  const more = () => setShown((n) => n + PAGE * 2);
  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (es) => { if (es.some((e) => e.isIntersecting)) more(); },
      { rootMargin: "600px 0px" },   // fetch ahead, so the grid never visibly stalls
    );
    io.observe(el);
    return () => io.disconnect();
  }, [shown, sorted.length]);

  if (q.isLoading || sorted.length === 0) return null;
  const visible = sorted.slice(0, shown);

  return (
    <section>
      <header className="library-all-bar">
        <div>
          <p className="page-eyebrow mb-1">Everything</p>
          <h2 className="font-display text-[1.7rem] sm:text-[2rem] leading-[1.15] tracking-tight">
            The whole Library
          </h2>
          <p className="font-grotesk text-[0.78rem] text-[var(--color-ink-soft)] mt-2 max-w-[60ch]">
            {sorted.length} documents, all of them. Keep going, or jump to the
            Documents tab to search and filter by tag.
          </p>
        </div>
        <div className="seg" role="group" aria-label="Sort">
          {SORTS.map((o) => (
            <button
              key={o.id}
              type="button"
              aria-pressed={sort === o.id}
              onClick={() => { setSort(o.id); setShown(PAGE); }}
            >
              {o.label}
            </button>
          ))}
        </div>
      </header>

      <ul className="library-all-grid">
        {visible.map((d) => (
          <li key={d.id}>
            <DocumentCard d={d} eyebrow="Document" />
          </li>
        ))}
      </ul>

      {shown < sorted.length ? (
        <>
          {/* Grows as you reach it. The button stays underneath for keyboard
              users and for the case where the observer never fires (a short
              viewport where the sentinel is already on screen). */}
          <div ref={sentinel} aria-hidden className="h-px" />
          <div className="mt-7 flex justify-center">
            <button type="button" className="btn-ghost" onClick={more}>
              Show more · {sorted.length - shown} left
            </button>
          </div>
        </>
      ) : (
        <p className="library-all-end">That’s all {sorted.length}</p>
      )}
    </section>
  );
}

function DocumentCard({ d, eyebrow }: { d: DocumentRef; eyebrow: string }) {
  return (
    <LibraryCard
      to={`/library/${d.slug}`}
      coverImage={d.coverImage}
      eyebrow={eyebrow}
      title={d.title}
      subtitle={d.subtitle}
      excerpt={d.excerpt}
    />
  );
}

function EmptyHint({ message }: { message: string }) {
  return (
    <p className="veda-empty is-dashed font-grotesk text-[0.85rem] text-[var(--color-ink-mute)] italic">
      {message}
    </p>
  );
}
